import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import { 
  loadVoiceMemos, 
  loadVoiceMemoBlob, 
  setCurrentlyPlaying, 
  createVoiceMemo, 
  updateVoiceMemoName, 
  deleteVoiceMemoThunk,
  setLoading
} from 'lib/redux/linkedin/voice-memos/slice';
import { LinkedInVoiceMemoMetadata } from 'interfaces/db';
import { getServicesManager } from 'services';
import { Typography } from 'interfaces/typography';
import { PrimaryActionButton } from 'utils/CommonUIComponents';
import { VoiceNoteList } from './VoiceNoteList';
import { VoiceNoteEditDialog } from './VoiceNoteEditDialog';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  Edit as EditIcon
} from '@mui/icons-material';

interface VoiceNote extends LinkedInVoiceMemoMetadata {
  blob?: Blob;
  url?: string;
  isPlaying?: boolean;
  isLoading?: boolean;
  error?: string;
}

export function VoiceNoteManagement() {
  const dispatch = useDispatch<AppDispatch>();
  const voiceNotes = useSelector((state: RootState) => state.linkedinVoiceNotes.vms) || [];
  const isLoading = useSelector((state: RootState) => state.linkedinVoiceNotes.loading);
  const currentlyPlaying = useSelector((state: RootState) => state.linkedinVoiceNotes.currentlyPlaying);
  const loadingStates = useSelector((state: RootState) => state.linkedinVoiceNotes.loadingStates);
  const userId = useSelector((state: RootState) => state.user.value?.user_id);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentVoiceNote, setCurrentVoiceNote] = useState<LinkedInVoiceMemoMetadata | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    file: null as Blob | null,
    recordedDuration: 0
  });
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedVoiceNoteId, setSelectedVoiceNoteId] = useState<string | null>(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [voiceNoteToDelete, setVoiceNoteToDelete] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    dispatch(loadVoiceMemos());
  }, [dispatch]);

  const handleOpenDialog = async (voiceNote?: LinkedInVoiceMemoMetadata | null) => {
    if (voiceNote) {
      const note = voiceNotes.find((n: VoiceNote) => n.linkedin_voice_memo_id === voiceNote.linkedin_voice_memo_id);
      if (!note?.blob) {
        try {
          await dispatch(loadVoiceMemoBlob(voiceNote.linkedin_voice_memo_id)).unwrap();
        } catch (error) {
          setNotification({
            open: true,
            message: 'Error loading voice note',
            severity: 'error'
          });
          return;
        }
      }
    }
    
    setCurrentVoiceNote(voiceNote || null);
    setFormData({
      name: voiceNote?.name || '',
      file: null,
      recordedDuration: 0
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentVoiceNote(null);
    setFormData({
      name: '',
      file: null,
      recordedDuration: 0
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, name: e.target.value });
  };

  const handleRecordingComplete = (blob: Blob, duration: number) => {
    setFormData({
      ...formData,
      file: blob,
      recordedDuration: duration
    });
  };

  const handleSubmit = async () => {
    try {
      if (currentVoiceNote) {
        if (formData.file) {
          // Replace the audio file
          const updatedVoiceNote = await getServicesManager().replaceLinkedInVoiceMemo(currentVoiceNote.linkedin_voice_memo_id, formData.file);
          if (updatedVoiceNote) {
            dispatch(loadVoiceMemos()); // Reload all voice notes to get the updated one
            setNotification({
              open: true,
              message: 'Voice note updated successfully',
              severity: 'success'
            });
          }
        }
        if (formData.name !== currentVoiceNote.name) {
          // Rename the voice note
          await dispatch(updateVoiceMemoName({ 
            id: currentVoiceNote.linkedin_voice_memo_id, 
            name: formData.name 
          }));
          setNotification({
            open: true,
            message: 'Voice note renamed successfully',
            severity: 'success'
          });
        }
      } else if (formData.file && formData.name) {
        // Create new voice note
        await dispatch(createVoiceMemo({ 
          blob: formData.file, 
          name: formData.name 
        }));
        setNotification({
          open: true,
          message: 'Voice note created successfully',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (error) {
      setNotification({
        open: true,
        message: 'Error saving voice note',
        severity: 'error'
      });
    }
  };

  const handleDeleteVoiceNote = (voiceNoteId: string, skipConfirmation: boolean = false) => {
    setVoiceNoteToDelete(voiceNoteId);
    
    if (skipConfirmation) {
      // Skip confirmation if it's already been confirmed in VoiceNoteTile
      confirmDelete(voiceNoteId);
    } else {
      // Show confirmation for deletions initiated from the action menu
      setDeleteDialogOpen(true);
    }
    
    handleCloseActionMenu();
  };

  const confirmDelete = async (voiceNoteId?: string) => {
    // Use the passed voiceNoteId if provided, otherwise use the one from state
    const idToDelete = voiceNoteId || voiceNoteToDelete;
    if (!idToDelete) return;

    try {
      await dispatch(deleteVoiceMemoThunk(idToDelete));
      setNotification({
        open: true,
        message: 'Voice note deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error instanceof Error ? error.message : 'Error deleting voice note',
        severity: 'error'
      });
    }
    setDeleteDialogOpen(false);
    setVoiceNoteToDelete(null);
  };

  const handleSaveEdit = (voiceNote: LinkedInVoiceMemoMetadata) => {
    dispatch(updateVoiceMemoName({ 
      id: voiceNote.linkedin_voice_memo_id, 
      name: voiceNote.name 
    }));
    setNotification({
      open: true,
      message: 'Voice note renamed successfully',
      severity: 'success'
    });
  };

  const handlePlayVoiceNote = async (id: string) => {
    if (currentlyPlaying === id) {
      if (audioRef.current) {
        audioRef.current.pause();
        dispatch(setCurrentlyPlaying(null));
      }
      return;
    }

    try {
      if (currentlyPlaying && audioRef.current) {
        audioRef.current.pause();
        dispatch(setCurrentlyPlaying(null));
      }

      dispatch(setLoading(true));

      let note = voiceNotes.find((n: VoiceNote) => n.linkedin_voice_memo_id === id);
      
      // If no blob, just load it and return
      if (!note?.blob) {
        try {
          const result = await dispatch(loadVoiceMemoBlob(id)).unwrap();
          if (!result.blob) {
            throw new Error('Failed to load audio blob');
          }
          return; // Don't auto-play, just return after loading
        } catch (error) {
          console.error('Error loading blob:', error);
          throw new Error('Failed to load audio');
        }
      }

      // If we have a blob, play it
      const audioUrl = note.blob ? URL.createObjectURL(note.blob) : '';

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
        audioRef.current.src = audioUrl;
        
        const loadPromise = new Promise((resolve, reject) => {
          if (!audioRef.current) return reject('No audio element');
          
          const loadHandler = () => {
            resolve(true);
            audioRef.current?.removeEventListener('loadeddata', loadHandler);
            audioRef.current?.removeEventListener('canplaythrough', loadHandler);
          };
          
          const errorHandler = (error: Event) => {
            URL.revokeObjectURL(audioUrl);
            reject(error);
            audioRef.current?.removeEventListener('error', errorHandler);
          };
          
          audioRef.current.addEventListener('loadeddata', loadHandler);
          audioRef.current.addEventListener('canplaythrough', loadHandler);
          audioRef.current.addEventListener('error', errorHandler);
        });

        try {
          await loadPromise;
          await audioRef.current.play();
          dispatch(setCurrentlyPlaying(id));
          
          audioRef.current.onended = () => {
            URL.revokeObjectURL(audioUrl);
            dispatch(setCurrentlyPlaying(null));
          };
          
          audioRef.current.onerror = () => {
            URL.revokeObjectURL(audioUrl);
            dispatch(setCurrentlyPlaying(null));
            setNotification({
              open: true,
              message: 'Error playing audio',
              severity: 'error'
            });
          };
        } catch (error) {
          URL.revokeObjectURL(audioUrl);
          console.error('Error playing audio:', error);
          dispatch(setCurrentlyPlaying(null));
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in handlePlayVoiceNote:', error);
      dispatch(setCurrentlyPlaying(null));
      setNotification({
        open: true,
        message: 'Error playing voice note',
        severity: 'error'
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLButtonElement>, voiceNoteId: string) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedVoiceNoteId(voiceNoteId);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedVoiceNoteId(null);
  };

  const handleRefresh = () => {
    dispatch(loadVoiceMemos());
  };

  const filteredVoiceNotes = voiceNotes?.filter((vn: VoiceNote) => {
    return vn.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="h-full w-full bg-gray-50 p-3 sm:p-4 overflow-auto">
      <audio 
        ref={audioRef} 
        onEnded={() => dispatch(setCurrentlyPlaying(null))}
        onError={() => {
          dispatch(setCurrentlyPlaying(null));
          setNotification({
            open: true,
            message: 'Error playing audio',
            severity: 'error'
          });
        }}
      />
      <div className="space-y-3 mx-auto max-w-5xl">
        {/* Header */}
        <section className="flex justify-between items-center">
          <div>
            <Typography variant="h3">Voice Note Management</Typography>
            <Typography variant="mediumParagraph" color="#6B7280">
              Create and manage reusable voice recordings
            </Typography>
          </div>
          <PrimaryActionButton
            icon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Voice Note
          </PrimaryActionButton>
        </section>

        {/* Search and Filter */}
        <section className="relative z-10 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              placeholder="Search voice notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  fontSize: '0.875rem',
                  '& fieldset': {
                    borderColor: 'rgb(229, 231, 235)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgb(107, 114, 128)'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgb(156, 163, 175)' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Tooltip title="Refresh voice notes">
              <IconButton 
                onClick={handleRefresh} 
                disabled={isLoading}
                sx={{
                  border: '1px solid rgb(229, 231, 235)',
                  borderRadius: 1,
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </section>

        {/* Voice Note List */}
        <VoiceNoteList
          voiceNotes={voiceNotes}
          filteredVoiceNotes={filteredVoiceNotes}
          userId={userId}
          currentlyPlaying={currentlyPlaying}
          loadingStates={loadingStates}
          searchTerm={searchTerm}
          onClearSearch={() => setSearchTerm('')}
          onPlayVoiceNote={handlePlayVoiceNote}
          onEditVoiceNote={handleSaveEdit}
          onOpenActionMenu={handleOpenActionMenu}
          onDeleteVoiceNote={handleDeleteVoiceNote}
        />

      {/* Create/Edit Dialog */}
        <VoiceNoteEditDialog
        open={openDialog} 
        onClose={handleCloseDialog}
        onSubmit={(name, blob, duration) => {
          // Create a new voice memo or update an existing one
          if (currentVoiceNote) {
            // Update existing voice note
            if (blob) {
              getServicesManager().replaceLinkedInVoiceMemo(currentVoiceNote.linkedin_voice_memo_id, blob)
                .then(() => {
                  dispatch(loadVoiceMemos());
                  setNotification({
                    open: true,
                    message: 'Voice note updated successfully',
                    severity: 'success'
                  });
                })
                .catch(error => {
                  setNotification({
                    open: true,
                    message: 'Error updating voice note',
                    severity: 'error'
                  });
                });
            }
            
            if (name !== currentVoiceNote.name) {
              dispatch(updateVoiceMemoName({ 
                id: currentVoiceNote.linkedin_voice_memo_id, 
                name: name
              }))
                .then(() => {
                  setNotification({
                    open: true,
                    message: 'Voice note renamed successfully',
                    severity: 'success'
                  });
                })
                .catch(error => {
                  setNotification({
                    open: true,
                    message: 'Error renaming voice note',
                    severity: 'error'
                  });
                });
            }
          } else {
            // Create new voice note
            dispatch(createVoiceMemo({ 
              blob: blob, 
              name: name
            }))
              .then(() => {
                setNotification({
                  open: true,
                  message: 'Voice note created successfully',
                  severity: 'success'
                });
              })
              .catch(error => {
                setNotification({
                  open: true,
                  message: 'Error creating voice note',
                  severity: 'error'
                });
              });
          }
          
          handleCloseDialog();
        }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            border: '1px solid rgb(229, 231, 235)',
            borderRadius: 1,
            mt: 1,
            minWidth: 160,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        {selectedVoiceNoteId && voiceNotes && (
          <>
            <MenuItem 
              onClick={() => {
                const voiceNote = voiceNotes.find((vn: VoiceNote) => vn.linkedin_voice_memo_id === selectedVoiceNoteId);
                if (voiceNote) handlePlayVoiceNote(voiceNote.linkedin_voice_memo_id);
                handleCloseActionMenu();
              }}
              sx={{
                py: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <PlayIcon fontSize="small" sx={{ mr: 1.5 }} />
              <Typography variant="mediumParagraph">Play</Typography>
            </MenuItem>
            <MenuItem 
              onClick={() => {
                const voiceNote = voiceNotes?.find((vn: VoiceNote) => vn.linkedin_voice_memo_id === selectedVoiceNoteId);
                if (voiceNote) {
                  // Convert VoiceNote to LinkedInVoiceMemoMetadata with proper type handling
                  const metadata: LinkedInVoiceMemoMetadata = {
                    linkedin_voice_memo_id: voiceNote.linkedin_voice_memo_id,
                    name: voiceNote.name,
                    user_id: voiceNote.user_id || '', // Ensure user_id is never undefined
                    team_id: voiceNote.team_id || '',
                    file_size: voiceNote.file_size
                  };
                  handleOpenDialog(metadata);
                }
              }}
              sx={{
                py: 1,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1.5 }} />
              <Typography variant="mediumParagraph">Rename</Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem 
              onClick={() => {
                if (selectedVoiceNoteId) handleDeleteVoiceNote(selectedVoiceNoteId, false);
              }}
              sx={{
                py: 1,
                color: 'rgb(239, 68, 68)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.04)'
                }
              }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
              <Typography variant="mediumParagraph">Delete</Typography>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 1,
            border: '1px solid rgb(229, 231, 235)',
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2.5,
          pb: 1
        }}>
          <Typography variant="h4" color="rgb(239, 68, 68)">
            Delete Voice Note
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5, pt: 1 }}>
          <Typography variant="mediumParagraph" color="rgb(75, 85, 99)">
            Are you sure you want to delete this voice note? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2.5,
          pt: 1.5,
          gap: 1
        }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ 
              color: 'rgb(75, 85, 99)',
              textTransform: 'none',
              borderColor: 'rgb(229, 231, 235)',
              '&:hover': {
                borderColor: 'rgb(107, 114, 128)',
                backgroundColor: 'transparent'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => confirmDelete()}
            variant="contained"
            disabled={isLoading}
            sx={{
              bgcolor: 'rgb(239, 68, 68)',
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgb(220, 38, 38)'
              },
              '&:disabled': {
                bgcolor: 'rgba(239, 68, 68, 0.5)'
              }
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
          severity={notification.severity}
          sx={{ 
            minWidth: '300px',
            borderRadius: 1,
            backgroundColor: notification.severity === 'success' ? 'rgb(22, 101, 52)' : 'rgb(153, 27, 27)',
            color: 'white',
            '.MuiAlert-icon': {
              color: 'white'
            },
            '.MuiAlert-action': {
              color: 'white',
              opacity: 0.8,
              '&:hover': {
                opacity: 1
              }
            },
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          <Typography variant="mediumParagraph" color="white" fontWeight={500}>
            {notification.message}
          </Typography>
        </Alert>
      </Snackbar>
      </div>
    </div>
  );
}
